import {
  confirmResetPassword,
  confirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  signIn,
  signOut,
  signUp,
} from 'aws-amplify/auth';
import { AuthError, type AuthSession } from '@emazy/shared-types';

import type { AuthService } from '@/features/auth/services/AuthService';

import {
  isCognitoUnauthenticatedError,
  isForgotPasswordPrivacyError,
  mapCognitoError,
} from './cognitoErrorMapper';
import { mapCognitoSession } from './cognitoSessionMapper';
import type { CognitoSessionLike, CognitoUserLike } from './types';

export type CognitoAuthSdk = {
  signIn: typeof signIn;
  signUp: typeof signUp;
  confirmSignUp: typeof confirmSignUp;
  resendSignUpCode: typeof resendSignUpCode;
  resetPassword: typeof resetPassword;
  confirmResetPassword: typeof confirmResetPassword;
  signOut: typeof signOut;
  getCurrentUser: typeof getCurrentUser;
  fetchAuthSession: typeof fetchAuthSession;
};

export const amplifyAuthSdk: CognitoAuthSdk = {
  signIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  signOut,
  getCurrentUser,
  fetchAuthSession,
};

const normalizeEmail = (email: string) => email.trim().toLowerCase();

export class CognitoAuthService implements AuthService {
  private refreshPromise: Promise<AuthSession | null> | undefined;

  constructor(private readonly sdk: CognitoAuthSdk = amplifyAuthSdk) {}

  async getCurrentSession(): Promise<AuthSession | null> {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = this.restoreSession();
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = undefined;
    }
  }

  private async restoreSession(): Promise<AuthSession | null> {
    try {
      const [user, session] = await Promise.all([
        this.sdk.getCurrentUser(),
        this.sdk.fetchAuthSession(),
      ]);
      if (!session.tokens) return null;
      return mapCognitoSession(user as CognitoUserLike, session as CognitoSessionLike);
    } catch (error) {
      if (isCognitoUnauthenticatedError(error)) return null;
      throw mapCognitoError(error, 'restore-session');
    }
  }

  async login(input: Parameters<AuthService['login']>[0]) {
    try {
      const result = await this.sdk.signIn({
        username: normalizeEmail(input.email),
        password: input.password,
      });
      const step = result.nextStep.signInStep;
      if (result.isSignedIn || step === 'DONE') {
        const session = await this.getCurrentSession();
        if (!session)
          throw new AuthError({
            code: 'session-expired',
            message: 'No Cognito session followed sign-in.',
          });
        return session;
      }
      if (step === 'CONFIRM_SIGN_UP')
        throw new AuthError({
          code: 'email-not-verified',
          message: 'Email confirmation is required.',
          field: 'email',
        });
      if (step === 'RESET_PASSWORD')
        throw new AuthError({
          code: 'password-reset-required',
          message: 'Password reset is required.',
          field: 'password',
        });
      if (import.meta.env.DEV) console.warn('Unsupported Cognito sign-in next step:', step);
      throw new AuthError({
        code: 'unsupported-auth-challenge',
        message: 'Unsupported Cognito sign-in challenge.',
      });
    } catch (error) {
      throw mapCognitoError(error, 'login');
    }
  }

  async register(input: Parameters<AuthService['register']>[0]) {
    const email = normalizeEmail(input.email);
    try {
      const result = await this.sdk.signUp({
        username: email,
        password: input.password,
        options: {
          userAttributes: {
            email,
            given_name: input.firstName.trim(),
            family_name: input.lastName.trim(),
          },
        },
      });
      return { email, verificationRequired: !result.isSignUpComplete };
    } catch (error) {
      throw mapCognitoError(error, 'register');
    }
  }

  async verifyEmail(input: Parameters<AuthService['verifyEmail']>[0]) {
    try {
      await this.sdk.confirmSignUp({
        username: normalizeEmail(input.email),
        confirmationCode: input.code,
      });
    } catch (error) {
      throw mapCognitoError(error, 'verify-email');
    }
  }

  async resendVerificationCode(input: Parameters<AuthService['resendVerificationCode']>[0]) {
    try {
      await this.sdk.resendSignUpCode({ username: normalizeEmail(input.email) });
    } catch (error) {
      throw mapCognitoError(error, 'resend-verification');
    }
  }

  async forgotPassword(input: Parameters<AuthService['forgotPassword']>[0]) {
    try {
      await this.sdk.resetPassword({ username: normalizeEmail(input.email) });
    } catch (error) {
      if (isForgotPasswordPrivacyError(error)) return;
      throw mapCognitoError(error, 'forgot-password');
    }
  }

  async resetPassword(input: Parameters<AuthService['resetPassword']>[0]) {
    try {
      await this.sdk.confirmResetPassword({
        username: normalizeEmail(input.email),
        confirmationCode: input.code,
        newPassword: input.newPassword,
      });
    } catch (error) {
      throw mapCognitoError(error, 'reset-password');
    }
  }

  async logout() {
    try {
      await this.sdk.signOut();
    } catch (error) {
      if (!isCognitoUnauthenticatedError(error)) throw mapCognitoError(error, 'logout');
    }
  }

  async getAccessToken(options: { forceRefresh?: boolean | undefined } = {}) {
    try {
      const session = await this.sdk.fetchAuthSession({
        forceRefresh: options.forceRefresh ?? false,
      });
      return session.tokens?.accessToken.toString() ?? null;
    } catch (error) {
      if (isCognitoUnauthenticatedError(error)) return null;
      throw mapCognitoError(error, 'access-token');
    }
  }
}
